import { ChangeEvent, FormEvent, SyntheticEvent, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import merge from 'lodash/merge';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Select from 'components/common/ReactSelect/Select';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { parseDuration } from 'common/util/time';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import ComboBox from 'components/common/ComboBox/ComboBox';
import Dialog from 'components/common/Dialog/Dialog';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { facetListState } from 'components/search/facets.state';
import { useFacetsNeeded } from 'hooks/useFacetsNeeded';

import { ADD_GRAPH_TO_DASHBOARD, EDIT_DASHBOARD_GRAPH, GET_CONFIGV2 } from 'components/dashboards/queries';
import {
  createUpdatedGraphVariables,
  DEFAULT_BAR_CHART_SLICES,
  DEFAULT_INTERVAL,
  MAX_BAR_CHART_SLICES,
} from 'components/dashboards/dashboardData';
import { DashboardJSONType, GraphConfigType, GraphPlotConfigType } from 'components/dashboards/types';
import GraphPlotList from 'components/dashboards/DashboardPage/GraphPlotList';

import { plotsStylesState } from 'styles/stylesProvider';
import './AddGraphModal.scss';

const emptyPlotList: DashboardJSONType[] = [];
const DEFAULT_BREAKDOWN_PLOT_MODEL: GraphPlotConfigType = {
  label: 'rate',
  facet: '',
  filter: '',
};

const DEFAULT_REFETCH_START_MS = 90 * K.MS_PER_MIN;

const initialFormValues = {
  title: '',
  graphStyle: K.CHART_TYPES.LINE,
  lineSmoothing: K.LINE_OPTIONS.STRAIGHT_LINES,
  breakdownFacet: '',
  barWidth: DEFAULT_INTERVAL,
  smoothingInterval: DEFAULT_INTERVAL,
  plots: emptyPlotList,
};

/**
 * Assign colors to plots, if they are missing.
 */
const setPlotColors = (plots: Array<GraphPlotConfigType>, plotColors: string[]): Array<GraphPlotConfigType> => {
  const newPlots = [] as Array<GraphPlotConfigType>;
  plots.forEach((plot, index) => {
    const newPlot = { ...plot };
    if (!newPlot.color) {
      newPlot.color = plotColors[index % plotColors.length];
    }
    newPlots.push(newPlot);
  });
  return newPlots;
};

export enum GraphType {
  STANDARD = 'standard',
  BREAKDOWN = 'breakdown',
}

// types
type InputGroupType = {
  label: string;
  value: string | number | undefined;
  placeholder?: string;
  colSize?: number;
  as: 'input' | 'textarea' | 'select';
  options?: Array<string>;
  name: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  testId?: ELEMENT;
};

type RadioSectionType = {
  label: string;
  value: string;
  testId?: ELEMENT;
};

type RadioGroupType = {
  label: string;
  radioSections: Array<RadioSectionType>;
  value: string | undefined;
  name: string;
  disabled?: boolean;
};

type AddGraphModalProps = {
  graphConfig?: GraphConfigType;
  showModal: boolean;
  graphType: string;
  handleClose: () => void;
  dashboardName: string;
  isAddNew: boolean;
  graphIndex?: number;
};

type BarWidth = {
  label: string;
  value: string;
};

/**
 * WIP:
 * 1) TODO: using graphIndex to edit is bad considering two users editing a graph. Need to a better way.
 *
 * Add Graph modal component is used to add a standard or breakdown graph to a dashboard.
 *
 * @prop  graphConfig    graph config object
 * @prop  showModal      controls the visibility of the modal
 * @prop  graphType      standard or breakdown (Enum)
 * @prop  handleClose    callback function to handle close. This sets the visibility of the modal to false.
 * @prop  dashboardName  the dashboard name used to refetch a query
 * @prop  isAddNew       if true, it uses add graph mutation
 * @prop  graphIndex     the index of the graph in the dashboard. Used to edit.
 */

function AddGraphModal({
  graphConfig,
  showModal = false,
  graphType,
  handleClose,
  dashboardName,
  isAddNew,
  graphIndex,
}: AddGraphModalProps): JSX.Element {
  const plotsStyles = useRecoilValue(plotsStylesState);
  const graphValuesForModel = merge({}, initialFormValues, graphConfig);
  const newPlotsForModel = graphType === GraphType.BREAKDOWN ? [DEFAULT_BREAKDOWN_PLOT_MODEL] : emptyPlotList;
  const plotsForModel = isAddNew ? newPlotsForModel : graphValuesForModel.plots;

  const [graphValues, setGraphValues] = useState<GraphConfigType>(graphValuesForModel);
  const [plots, setPlots] = useState<Array<GraphPlotConfigType>>(plotsForModel);
  const [validated, setValidated] = useState(false);
  const plotsWithColors = setPlotColors(plots, plotsStyles);
  const [plotsToDisplay, setPlotsToDisplay] = useState<Array<GraphPlotConfigType>>(plotsWithColors);
  const [isNumBars, setIsNumBars] = useState(!!graphValuesForModel.numBars);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { endTime } = queryTimeRange;
  // Only refetch last hour and a half of data to optimize server performance
  const startTime = String(Number(endTime) - DEFAULT_REFETCH_START_MS);
  // TODO: instead of refetching the entire dashboard, update the cache instead
  const [saveGraphToDashboard] = useMutation(isAddNew ? ADD_GRAPH_TO_DASHBOARD : EDIT_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  // make sure we have facets
  const facetList = useRecoilValue(facetListState);
  useFacetsNeeded();

  /**
   * Add graph to dashboard.
   */
  const handleSaveGraph = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (!form.checkValidity() || (graphType !== GraphType.BREAKDOWN && !plots.length)) {
      setValidated(true);
    } else {
      // We make a copy of the values so react doesn't object to our getting
      // rid of some values we don't need
      const graphValuesCopy = { ...graphValues };
      if (graphValuesCopy.graphStyle === K.CHART_TYPES.STACKED_BAR) {
        if (isNumBars) {
          delete graphValuesCopy.barWidth;
        } else {
          delete graphValuesCopy.numBars;
        }
      }

      const variables = createUpdatedGraphVariables(dashboardName, graphValuesCopy, graphIndex, plots, tabName);
      saveGraphToDashboard({ variables })
        .then(handleClose)
        .catch((error) => {
          setPageAlertConfig({ message: error.message, autoHide: true });
        });
    }
  };

  /**
   * Update the state with what user changed.
   */

  const handleFormChange = (event: SyntheticEvent): void => {
    const eventTarget = event.target as HTMLInputElement;
    const newGraphValues = { ...graphValues, [eventTarget.name]: eventTarget.value };
    setGraphValues(newGraphValues);
  };

  const handleBreakdownFacetChange = (facetVariable: string): void => {
    const newGraphValues = { ...graphValues, breakdownFacet: facetVariable };
    setGraphValues(newGraphValues);
  };

  const handleFacetChange = (facetVariable: string): void => {
    const [breakdownPlot] = [...plots];
    breakdownPlot.facet = facetVariable;
    setPlots([breakdownPlot]);
  };

  const handleBarWidthChange = (width: string): void => {
    const newGraphValues = { ...graphValues, barWidth: width };
    setGraphValues(newGraphValues);
  };

  const handleNumBarsChange = (): void => {
    // If first time toggling num bars selection, num bars hasn't been set yet in graphValues state.
    // Set it so it submits properly with form submission.
    if (!graphValues.numBars) {
      const newGraphValues = { ...graphValues, numBars: String(DEFAULT_BAR_CHART_SLICES) };
      setGraphValues(newGraphValues);
    }
    setIsNumBars(true);
  };

  const handleFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const filterInput = e.target.value;
    const [breakdownPlot] = [...plots];
    breakdownPlot.filter = filterInput;
    setPlots([breakdownPlot]);
  };

  /**
   * A helper function to generate a radio input group JSX element for this dialog.
   */
  const generateRadioGroup = (radioGroup: RadioGroupType): JSX.Element => (
    <Form.Group>
      <Form.Row className="AddGraphModal__RadioRow">
        <Form.Label column xs={2} className="AddGraphModal__RowLabel">
          {radioGroup.label}
        </Form.Label>
        <Col xs={10} className="AddGraphModal__RadioOptions">
          {radioGroup.radioSections.map((radioSection, index) => {
            const id = `AddGraphModal__${radioGroup.label}--${index}`;
            return (
              <CheckboxRadio
                id={id}
                key={id}
                name={radioGroup.name}
                value={radioSection.value}
                onChange={handleFormChange}
                type="radio"
                label={radioSection.label}
                checked={radioGroup.value === radioSection.value}
                disabled={radioGroup.disabled}
                testId={radioSection.testId}
              />
            );
          })}
        </Col>
      </Form.Row>
    </Form.Group>
  );

  /**
   * A helper function to generate an input group JSX element for this dialog.
   */
  const generateInputGroup = (
    inputGroup: InputGroupType,
    inputGroup2: InputGroupType | undefined = undefined,
  ): JSX.Element => {
    const options = inputGroup.options?.map((interval) => {
      const testId = createTestId(ELEMENT.dashboardAddGraphInterval, parseDuration(interval) / K.MS_PER_MIN);
      return (
        <option value={interval} key={interval} data-test-id={testId}>
          {interval}
        </option>
      );
    });

    return (
      <Form.Group controlId="titleLabel">
        <Form.Row>
          <Form.Label column xs={2} className="AddGraphModal__RowLabel">
            {inputGroup.label}
          </Form.Label>
          <Col xs={inputGroup.colSize}>
            <Form.Control
              required={inputGroup.required}
              onChange={inputGroup.onChange || handleFormChange}
              as={inputGroup.as}
              name={inputGroup.name}
              defaultValue={inputGroup.value}
              placeholder={inputGroup.placeholder}
              disabled={inputGroup.disabled}
              data-test-id={inputGroup.testId}
            >
              {options}
            </Form.Control>
            <Form.Control.Feedback type="invalid">{`${inputGroup.label} is required`}</Form.Control.Feedback>
          </Col>

          {inputGroup2 && (
            <>
              <Form.Label column xs={2} className="AddGraphModal__RowLabel">
                {inputGroup2.label}
              </Form.Label>
              <Col xs={inputGroup.colSize}>
                <Form.Control
                  required={inputGroup2.required}
                  onChange={handleFormChange}
                  as={inputGroup2.as}
                  name={inputGroup2.name}
                  defaultValue={inputGroup2.value}
                  placeholder={inputGroup2.placeholder}
                  disabled={inputGroup2.disabled}
                  data-test-id={inputGroup2.testId}
                />
                <Form.Control.Feedback type="invalid">{`${inputGroup2.label} is required`}</Form.Control.Feedback>
              </Col>
            </>
          )}
        </Form.Row>
      </Form.Group>
    );
  };

  const updatePlotsToDisplay = (newPlots: Array<GraphPlotConfigType>): void => {
    const newPlotsToDisplay = setPlotColors(newPlots, plotsStyles);
    setPlotsToDisplay(newPlotsToDisplay);
  };

  /**
   * Delete a plot from the current list by index.
   * If index is not present, do nothing.
   */
  const deletePlotByIndex = (index: number): void => {
    const newPlots = [...plots];
    newPlots.splice(index, 1);
    setPlots(newPlots);
    updatePlotsToDisplay(newPlots);
  };

  /**
   * Handle add/edit plot modal submit callback.
   *
   * @param index           The index of the plot.
   * @param incomingPlot    A new plot model.
   */
  const editExistingPlot = (index: number, incomingPlot: GraphPlotConfigType): void => {
    const newPlots = [...plots];
    newPlots[index] = incomingPlot;
    setPlots(newPlots);
    updatePlotsToDisplay(newPlots);
  };

  const createNewPlot = (incomingPlot: GraphPlotConfigType): void => {
    const newPlots = [...plots];
    newPlots.push(incomingPlot);
    setPlots(newPlots);
    updatePlotsToDisplay(newPlots);
  };

  /**
   * input and radio config objects used with
   * generateRadioGroup() and generateInputGroup() to generate JSX elements.
   */

  const titleInputGroup: InputGroupType = {
    label: 'Title',
    value: graphValues.title || graphValues.label, // graphs that were saved from outside of dashboard use label in the config
    placeholder: 'What to call this graph',
    colSize: 10,
    as: 'input',
    name: 'title',
    required: true,
    testId: ELEMENT.dashboardAddGraphTitle,
  };

  const filterInputGroup: InputGroupType = {
    label: 'Filter',
    value: plots[0]?.filter || '',
    placeholder: 'Search filter text (optional)',
    colSize: 10,
    as: 'textarea',
    name: 'filter',
    onChange: handleFilterChange,
    testId: ELEMENT.dashboardAddGraphFilter,
  };

  const chartTypeRadioGroup: RadioGroupType = {
    label: 'Type',
    value: graphValues.graphStyle,
    name: 'graphStyle',
    radioSections: [
      { label: 'Line Chart', value: K.CHART_TYPES.LINE, testId: ELEMENT.dashboardAddGraphLine },
      { label: 'Area Chart', value: K.CHART_TYPES.STACKED, testId: ELEMENT.dashboardAddGraphStacked },
      { label: 'Stacked Bar Chart', value: K.CHART_TYPES.STACKED_BAR, testId: ELEMENT.dashboardAddGraphStackedBar },
    ],
  };

  const lineTypeRadioGroup: RadioGroupType = {
    label: 'Lines',
    value: graphValues.lineSmoothing,
    name: 'lineSmoothing',
    disabled: graphValues.graphStyle === K.CHART_TYPES.STACKED_BAR,
    radioSections: [
      { label: 'Straight Lines', value: K.LINE_OPTIONS.STRAIGHT_LINES, testId: ELEMENT.dashboardAddGraphStraight },
      { label: 'Smooth Curves', value: K.LINE_OPTIONS.SMOOTH_CURVES, testId: ELEMENT.dashboardAddGraphSmooth },
    ],
  };

  const dialogFooter = {
    rightItems: [
      <Button
        variant={ButtonVariant.PRIMARY}
        type="submit"
        testId={ELEMENT.dashboardAddGraphOk}
        submitForm={K.DIALOG_FORM_IDS.ADD_GRAPH_MODAL_FORM_ID}
      >
        OK
      </Button>,
      <Button variant={ButtonVariant.SECONDARY} onClick={handleClose} testId={ELEMENT.dashboardAddGraphCancel}>
        Cancel
      </Button>,
    ],
  };

  const barWidthOptions = K.INTERVAL_OPTIONS.map((interval) => ({
    value: interval,
    label: interval,
  }));
  const autoBar = { value: 'auto', label: 'Auto' };
  barWidthOptions.unshift(autoBar);

  const currentBarWidth = barWidthOptions.find((barWidth) => barWidth.value === graphValues.barWidth) || autoBar;

  return (
    <Dialog
      title={isAddNew ? 'Add Graph' : 'Edit Graph'}
      show={showModal}
      close={handleClose}
      footer={dialogFooter}
      scrollable={true}
    >
      <Form
        className="AddGraphModal"
        noValidate
        validated={validated}
        onSubmit={handleSaveGraph}
        id={K.DIALOG_FORM_IDS.ADD_GRAPH_MODAL_FORM_ID}
      >
        {generateInputGroup(titleInputGroup)}
        {generateRadioGroup(chartTypeRadioGroup)}

        {graphValues.graphStyle !== K.CHART_TYPES.STACKED_BAR && generateRadioGroup(lineTypeRadioGroup)}

        {graphValues.graphStyle === K.CHART_TYPES.STACKED_BAR && (
          <Form.Group>
            <Form.Row className="AddGraphModal__StackedInputs">
              <Form.Label column xs={2} className="AddGraphModal__RowLabel">
                Bars
              </Form.Label>
              <Col xs={10} className="AddGraphModal__RadioOptions">
                <CheckboxRadio
                  id="intervalTypeAuto"
                  key="intervalTypeAuto"
                  name="intervalType"
                  value="auto"
                  onChange={(): void => handleNumBarsChange()}
                  type="radio"
                  checked={isNumBars}
                />
                <Form.Control
                  className="AddGraphModal__NumBars"
                  onChange={handleFormChange}
                  name="numBars"
                  value={graphValues.numBars || DEFAULT_BAR_CHART_SLICES}
                  disabled={!isNumBars}
                  placeholder={`${DEFAULT_BAR_CHART_SLICES}`}
                  type="number"
                  min="1"
                  max={MAX_BAR_CHART_SLICES}
                />
                bars
                <Form.Control.Feedback type="invalid">
                  &nbsp;&nbsp;&nbsp;Invalid number of bars (1-500 only)
                </Form.Control.Feedback>
              </Col>
            </Form.Row>
            <Form.Row className="AddGraphModal__StackedInputs">
              <Form.Label column xs={2} className="AddGraphModal__RowLabel" />
              <Col xs={10} className="AddGraphModal__RadioOptions">
                <CheckboxRadio
                  id="intervalTypeCustom"
                  key="intervalTypeCustom"
                  name="intervalType"
                  value="custom"
                  onChange={(): void => setIsNumBars(!isNumBars)}
                  type="radio"
                  checked={!isNumBars}
                />
                <Select
                  options={barWidthOptions}
                  isDisabled={isNumBars}
                  value={{ value: currentBarWidth.value, label: currentBarWidth.label }}
                  onChange={(barWidth) => {
                    const { value } = barWidth as BarWidth;
                    handleBarWidthChange(value);
                  }}
                />
                {graphValues.barWidth !== 'auto' && 'per bar'}
              </Col>
            </Form.Row>
          </Form.Group>
        )}

        {graphType === GraphType.BREAKDOWN && (
          <Form.Group controlId="breakdownFacet">
            <Form.Row>
              <Form.Label column xs={2} className="AddGraphModal__RowLabel">
                Breakdown
              </Form.Label>
              <Col xs={6}>
                <ComboBox
                  value={graphValues.breakdownFacet || ''}
                  items={facetList}
                  onChange={handleBreakdownFacetChange}
                  required
                  testId={ELEMENT.dashboardAddGraphBreakdown}
                  invalidError="Which field's values to break down is required"
                />
              </Col>
            </Form.Row>
          </Form.Group>
        )}

        {graphType === GraphType.BREAKDOWN && (
          <Form.Group controlId="breakdownFacet">
            <Form.Row>
              <Form.Label column xs={2} className="AddGraphModal__RowLabel">
                Field
              </Form.Label>
              <Col xs={6}>
                <ComboBox
                  value={plots[0]?.facet || ''}
                  items={facetList}
                  onChange={handleFacetChange}
                  testId={ELEMENT.dashboardAddGraphFacet}
                />
              </Col>
            </Form.Row>
          </Form.Group>
        )}

        {graphType === GraphType.BREAKDOWN && generateInputGroup(filterInputGroup)}

        {graphType === GraphType.STANDARD && (
          <Form.Group>
            <Form.Row>
              <Form.Label column xs={2} className="AddGraphModal__RowLabel">
                Plots
              </Form.Label>
              <Col xs={10}>
                <GraphPlotList
                  config={graphConfig as GraphConfigType}
                  plotsToDisplay={plotsToDisplay}
                  unSavedPlots={plots}
                  fullPage={false}
                  addPlotCallback={createNewPlot}
                  editPlotCallback={editExistingPlot}
                  deletePlotCallback={deletePlotByIndex}
                />
              </Col>
            </Form.Row>
            {validated && plots.length === 0 && <div className="AddGraphModal__PlotsErrorMsg">Please add a plot</div>}
          </Form.Group>
        )}
      </Form>
    </Dialog>
  );
}

export default AddGraphModal;
